import { CalendarDays } from 'lucide-react'
import type { ProposalMeetingRequestDto } from '@lib/dto/proposal-meeting.dto'
import { formatDateTime } from '@lib/helpers/format'
import { businessYear } from '@/helpers/businessDay'
import { errorMessage } from '@/helpers/logger'
import { useMeetingRequests } from '@/hooks/useProposalMeetings'

/**
 * "Wednesday, October 21", with the year only when it is not this one.
 *
 * Read as a calendar day in UTC, because that is what it is: the client
 * picked "the 21st", and formatting it through the viewer's time zone would
 * show the 20th to everyone in Texas.
 */
function formatMeetingDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`)
  const thisYear = date.getUTCFullYear() === businessYear()
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...(thisYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  }).format(date)
}

function MeetingRequestLine({
  request,
}: {
  request: ProposalMeetingRequestDto
}) {
  return (
    <li className="flex items-start gap-1.5 text-xs">
      <CalendarDays className="mt-0.5 h-3 w-3 shrink-0 text-brand" />
      <span className="text-muted-text">
        <span className="font-medium text-primary-text">
          Board meeting requested for {formatMeetingDay(request.meetingDate)}
        </span>
        {request.requestedByEmail === null ? null : (
          <>
            {' · from '}
            <span className="text-secondary-text">
              {request.requestedByEmail}
            </span>
          </>
        )}
        {' · '}
        {formatDateTime(request.createdAt)}
        {request.note === null ? null : (
          <span className="block text-secondary-text">“{request.note}”</span>
        )}
      </span>
    </li>
  )
}

/**
 * Board meetings the client asked someone to attend.
 *
 * Shown beside the deliveries because it is the same kind of fact: something
 * the client did with the link. The request also emailed the owner, but an
 * email can go astray; this is the record that does not.
 *
 * Renders nothing while it loads and when there is no request, because an
 * empty heading on every sent proposal would be noise. A failed fetch is
 * said: silence there looks like nobody asked.
 */
export function ProposalMeetingRequestList({
  versionId,
  isSent,
}: {
  versionId: string
  isSent: boolean
}) {
  const requests = useMeetingRequests(isSent ? versionId : '')
  if (!isSent) return null
  if (requests.isError) {
    return (
      <p className="text-xs text-danger">
        {errorMessage(
          requests.error,
          'Board meeting requests could not be loaded.',
        )}
      </p>
    )
  }
  if (requests.data === undefined || requests.data.length === 0) {
    return null
  }

  return (
    <ul className="space-y-1.5">
      {requests.data.map((request) => (
        <MeetingRequestLine key={request.id} request={request} />
      ))}
    </ul>
  )
}
